// src/components/NFT/BoosterNFTMinter.js
import React, { useState, useContext } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Modal, 
  IconButton, 
  Card, 
  CardContent, 
  Grid, 
  Divider, 
  Paper, 
  Slider, 
  ButtonGroup, 
  Popover, 
  Snackbar, 
  Alert, 
  CircularProgress,
  Badge
} from '@mui/material';
import { 
  AddCircleOutline as AddIcon, 
  RemoveCircleOutline as RemoveIcon, 
  Close as CloseIcon
} from '@mui/icons-material';

import { BoosterNFTContext } from '../../context/BoosterNFTContext';
import { WalletContext } from '../../context/WalletContext';
import { NETWORK_CONFIG } from '../../contracts/constants';
import { ethers } from 'ethers';
import CIDImage from '../../assets/CID.png'; // CID image imported for share snackbar

// Booster NFT rarity information
const RARITY_INFO = [
  { name: "Common", chance: "50%", boost: "5", color: "#a0a0a0", icon: "" },
  { name: "Uncommon", chance: "30%", boost: "10", color: "#00cc00", icon: "" },
  { name: "Rare", chance: "15%", boost: "20", color: "#0088ff", icon: "" },
  { name: "Legendary", chance: "5%", boost: "40", color: "#ff8800", icon: "" }
];

// Exact mint price in Wei: 0.01 ETH
const MINT_PRICE_PER_NFT_WEI = ethers.utils.parseEther("0.01");

const BoosterNFTMinter = ({ compact = false }) => {
  const { 
    boosterNFTs, 
    mintNFTs,
    isMinting,
    totalBoostPercentage,
    isInitialized
  } = useContext(BoosterNFTContext);
  const { account, chainId } = useContext(WalletContext);
  
  const [openModal, setOpenModal] = useState(false);
  const [mintAmount, setMintAmount] = useState(1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showShareSnackbar, setShowShareSnackbar] = useState(false);
  const [lastMintAmount, setLastMintAmount] = useState(0);
  
  const handleCloseModal = () => setOpenModal(false);
  const handleCloseShareSnackbar = () => setShowShareSnackbar(false);
  
  // Handle popover open/close
  const handlePopoverClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  
  // Handle minting NFTs
  const handleMint = async () => {
    try {
      const success = await mintNFTs(mintAmount);
      if (success) {
        setLastMintAmount(mintAmount);
        setShowShareSnackbar(true);
      }
    } catch (error) {
      console.error("Error minting NFTs:", error);
    }
  };
  
  // Share on X function without image in tweet text
  const handleShareOnX = () => {
    const tweetText = `I just minted ${lastMintAmount} Booster NFT${lastMintAmount !== 1 ? 's' : ''} to boost my $CASH rewards! Join the revolution @_CASHisDEAD_`;
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(shareUrl, '_blank');
    setShowShareSnackbar(false);
  };
  
  const handleSliderChange = (event, newValue) => {
    setMintAmount(newValue);
  };
  
  const incrementAmount = () => {
    setMintAmount(prev => Math.min(prev + 1, 10));
  };
  
  const decrementAmount = () => {
    setMintAmount(prev => Math.max(prev - 1, 1));
  };
  
  const isCorrectNetwork = Number(chainId) === NETWORK_CONFIG.chainId;
  const hasBoosterNFTs = boosterNFTs && boosterNFTs.length > 0;

  // Calculate total price in Wei, then convert to ETH for display
  const totalPriceWei = MINT_PRICE_PER_NFT_WEI.mul(mintAmount);
  const totalPriceEth = parseFloat(ethers.utils.formatEther(totalPriceWei));

  // Use the totalBoostPercentage from context
  const currentBoost = totalBoostPercentage;

  // --- COMPACT VIEW (POPOVER) ---
  if (compact) {
    if (!account || !isCorrectNetwork) {
      return null; // Hide if not connected or on wrong network
    }
    
    return (
      <>
        <Box sx={{ display: 'inline-block' }}>
          <Badge
            badgeContent={hasBoosterNFTs ? boosterNFTs.length : 0}
            color="primary"
            sx={{ 
              '& .MuiBadge-badge': { 
                fontSize: '0.6rem',
                minWidth: '16px',
                height: '16px',
                padding: '0 4px'
              }
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handlePopoverClick}
              sx={{ 
                fontSize: '0.7rem',
                py: 0.5,
                px: 1,
                minWidth: 0,
                whiteSpace: 'nowrap',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: '#ffcc00',
                  color: '#1a1a1a',
                  boxShadow: '0 0 8px #ffcc00',
                }
              }}
            >
              Boost
            </Button>
          </Badge>
        </Box>
        
        <Popover
          id="boost-popover"
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          onClose={handlePopoverClose}
          PaperProps={{
            sx: {
              bgcolor: '#121212',
              border: '1px solid #333',
              borderRadius: 1,
              color: 'white',
              p: 2,
              maxWidth: 300
            }
          }}
        >
          <Typography variant="subtitle1" gutterBottom sx={{ color: '#ffcc00', fontWeight: 'bold' }}>
            $CASH Booster NFT Bonuses:
          </Typography>
          <Typography variant="body2" sx={{ pl: 1, mb: 2, color: 'white' }}>
            • Mining Reward Boost<br />• LP Staking Boost
          </Typography>
          {hasBoosterNFTs && (
            <Typography variant="caption" sx={{ color: 'rgba(255, 204, 0, 0.7)', mb: 2, display: 'block' }}>
              Current boost: {currentBoost}% <span style={{ fontStyle: 'italic', color: '#ffffff' }}>(max 100%)</span>
            </Typography>
          )}
          <Divider sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="body1" sx={{ mr: 2, color: 'white', fontWeight: 'bold' }}>
                Quantity: {mintAmount}
              </Typography>
              <ButtonGroup 
                size="medium" 
                sx={{ 
                  ml: 'auto', 
                  '.MuiButtonGroup-grouped': { 
                    minWidth: '40px', 
                    height: '36px', 
                    transition: 'background-color 0.2s ease-in-out',
                  } 
                }}
              >
                <Button 
                  onClick={decrementAmount} 
                  disabled={mintAmount <= 1 || isMinting}
                  sx={{ 
                    color: 'white', 
                    borderColor: '#ffcc00', 
                    bgcolor: 'rgba(255, 204, 0, 0.1)',
                    '&:hover': { bgcolor: 'rgba(255, 204, 0, 0.3)' },
                  }}
                >
                  <RemoveIcon />
                </Button>
                <Button 
                  onClick={incrementAmount} 
                  disabled={mintAmount >= 10 || isMinting}
                  sx={{ 
                    color: 'white', 
                    borderColor: '#ffcc00', 
                    bgcolor: 'rgba(255, 204, 0, 0.1)',
                    '&:hover': { bgcolor: 'rgba(255, 204, 0, 0.3)' },
                  }}
                >
                  <AddIcon />
                </Button>
              </ButtonGroup>
            </Box>
            <Typography variant="body2" sx={{ mb: 1, color: 'white' }}>
              Price: <strong>0.01 ETH per NFT</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'white' }}>
              Total: <strong>{totalPriceEth.toFixed(3)} ETH</strong>
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleMint}
                disabled={isMinting || !isInitialized || mintAmount <= 0}
                sx={{
                  backgroundColor: '#ff3333',
                  '&:hover': { backgroundColor: '#cc0000' },
                  color: 'white',
                  fontWeight: 'bold',
                  mb: 1
                }}
                startIcon={isMinting ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
              >
                {isMinting ? (
                  <>
                    <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                    Minting...
                  </>
                ) : (
                  `Mint ${mintAmount} NFT${mintAmount !== 1 ? 's' : ''}`
                )}
              </Button>
            </Box>
          </Box>
          <Divider sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
          <Typography variant="subtitle2" sx={{ color: '#ffcc00', fontWeight: 'bold', mb: 1 }}>
            Rarity Tiers & Boosts
          </Typography>
          {RARITY_INFO.map((rarity, index) => (
            <Typography key={index} variant="caption" sx={{ color: 'white', display: 'block' }}>
              {rarity.name}: +{rarity.boost}% ({rarity.chance} drop chance)
            </Typography>
          ))}
        </Popover>
        
        {/* Share on X Snackbar */}
        <Snackbar 
          open={showShareSnackbar} 
          autoHideDuration={10000} 
          onClose={handleCloseShareSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity="success"
            sx={{ 
              width: '100%', 
              bgcolor: '#121212', 
              color: 'white',
              border: '1px solid #00acee',
              '.MuiAlert-icon': { color: '#00acee' }
            }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleShareOnX}
              >
                Share
              </Button>
            }
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
              <img src={CIDImage} alt="CID" style={{ maxWidth: '80px', marginBottom: '8px' }} />
            </Box>
            Successfully minted {lastMintAmount} Booster NFT{lastMintAmount !== 1 ? 's' : ''}!
          </Alert>
        </Snackbar>
      </>
    );
  }
  
  // --- FULL MODAL VIEW ---
  const handleOpenModal = () => setOpenModal(true);

  // Only show "Mint Booster NFTs" button if connected and on the right network
  if (!compact && account && isCorrectNetwork) {
    return (
      <>
        <Button
          variant="contained"
          onClick={handleOpenModal}
          sx={{
            backgroundColor: '#ff3333',
            '&:hover': { backgroundColor: '#cc0000' },
            color: 'white',
            fontWeight: 'bold',
            mb: 1
          }}
        >
          Mint Booster NFTs
        </Button>

        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 600 },
            maxHeight: '90vh',
            bgcolor: 'var(--background-light)',
            border: '2px solid var(--primary-color)',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            overflow: 'auto'
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" component="h2" sx={{ color: 'var(--primary-color)' }}>
                Booster NFTs
              </Typography>
              <IconButton onClick={handleCloseModal} size="small" sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Typography variant="body1" sx={{ mb: 2, color: '#ffffff' }}>
              Booster NFTs increase your rewards from <strong>Mining Events</strong> and <strong>LP Staking</strong>. 
              The maximum boost is capped at <strong>100%</strong>.
            </Typography>
            
            {hasBoosterNFTs && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(255, 204, 0, 0.1)', border: '1px solid rgba(255, 204, 0, 0.3)' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ffcc00' }}>
                  You currently have {boosterNFTs.length} Booster NFT{boosterNFTs.length !== 1 ? 's' : ''} 
                  with a total boost of {currentBoost}%
                  <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#ffffff', ml: 1 }}>
                    (max 100%)
                  </Typography>
                </Typography>
              </Paper>
            )}
            
            <Typography variant="h6" sx={{ mb: 2, mt: 2, color: '#ffffff' }}>Rarity Tiers & Boosts</Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {RARITY_INFO.map((rarity, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card sx={{ 
                    height: '100%',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    border: `1px solid ${rarity.color}`,
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.03)',
                      boxShadow: `0 0 10px ${rarity.color}, 0 0 20px ${rarity.color}`,
                      borderColor: `${rarity.color}`
                    }
                  }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6" sx={{ color: rarity.color, fontWeight: 'bold' }}>
                          {rarity.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: rarity.color }}>
                          {rarity.icon}
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 1.5, bgcolor: rarity.color, opacity: 0.3 }} />
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#ffffff' }}>
                        <strong>Drop Chance:</strong> {rarity.chance}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#ffffff' }}>
                        <strong>Mining Boost:</strong> +{rarity.boost}%
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#ffffff' }}>
                        <strong>LP Yield:</strong> +{rarity.boost}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            <Divider sx={{ my: 3, borderColor: 'var(--primary-color)' }} />
            
            <Box sx={{ bgcolor: 'rgba(0,0,0,0.3)', p: 3, borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#ffffff' }}>Mint Booster NFTs</Typography>
              
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography id="mint-amount-slider" gutterBottom sx={{ mr: 2, color: '#ffffff' }}>
                    Quantity: {mintAmount}
                  </Typography>
                  <ButtonGroup size="small" sx={{ ml: 'auto' }}>
                    <Button onClick={decrementAmount} disabled={mintAmount <= 1}
                      sx={{
                        backgroundColor: 'rgba(255, 204, 0, 0.1)',
                        borderColor: '#ffcc00',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 204, 0, 0.3)',
                        },
                      }}
                    >
                      <RemoveIcon fontSize="small" />
                    </Button>
                    <Button onClick={incrementAmount} disabled={mintAmount >= 10}
                      sx={{
                        backgroundColor: 'rgba(255, 204, 0, 0.1)',
                        borderColor: '#ffcc00',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 204, 0, 0.3)',
                        },
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </Button>
                  </ButtonGroup>
                </Box>
                
                <Slider
                  value={mintAmount}
                  onChange={handleSliderChange}
                  aria-labelledby="mint-amount-slider"
                  valueLabelDisplay="auto"
                  step={1}
                  marks
                  min={1}
                  max={10}
                  sx={{ 
                    color: '#ffcc00',
                    '& .MuiSlider-valueLabel': {
                      backgroundColor: '#ffcc00',
                      color: 'black'
                    }
                  }}
                />
                
                <Typography variant="body2" sx={{ mt: 1, mb: 2, color: '#ffffff' }}>
                  Total Price: <strong>{totalPriceEth.toFixed(3)} ETH</strong>
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleMint}
                    disabled={isMinting || !isInitialized || mintAmount <= 0}
                    sx={{
                      backgroundColor: '#ff3333',
                      '&:hover': { backgroundColor: '#cc0000' },
                      color: 'white',
                      fontWeight: 'bold',
                      mb: 1
                    }}
                    startIcon={isMinting ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
                  >
                    {isMinting ? (
                      <>
                        <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                        Minting...
                      </>
                    ) : (
                      `Mint ${mintAmount} NFT${mintAmount !== 1 ? 's' : ''}`
                    )}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Modal>
        
        {/* Share on X Snackbar */}
        <Snackbar 
          open={showShareSnackbar} 
          autoHideDuration={10000} 
          onClose={handleCloseShareSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity="success"
            sx={{ 
              width: '100%', 
              bgcolor: '#121212', 
              color: 'white',
              border: '1px solid #00acee',
              '.MuiAlert-icon': { color: '#00acee' }
            }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleShareOnX}
              >
                Share
              </Button>
            }
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
              <img src={CIDImage} alt="CID" style={{ maxWidth: '80px', marginBottom: '8px' }} />
            </Box>
            Successfully minted {lastMintAmount} Booster NFT{lastMintAmount !== 1 ? 's' : ''}!
          </Alert>
        </Snackbar>
      </>
    );
  }

  return null;
};

export default BoosterNFTMinter;